import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import { authAPI } from '../../services/api';
import { redirectByRole } from '../../utils/auth';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionContext';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const Login = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated, user } = useAuth();
  const { refreshPermissions } = usePermissions();
  const passwordRef = useRef(null);
  const captchaRef = useRef(null);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [requiresCaptcha, setRequiresCaptcha] = useState(false);
  const [captchaValue, setCaptchaValue] = useState(null);

  // Redirect if already logged in
  useEffect(() => {
    const isAuth = isAuthenticated();
    const userRole = user?.role;

    if (isAuth && user) {
      redirectByRole(userRole, navigate, user?.user_type);
    }
  }, [navigate, isAuthenticated, user]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    if (submitError) {
      setSubmitError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setIsSubmitting(true);

    try {
      const loginData = { ...formData };

      // Include CAPTCHA token if CAPTCHA was triggered and completed
      if (requiresCaptcha && RECAPTCHA_SITE_KEY) {
        if (!captchaValue) {
          setSubmitError('Please complete the CAPTCHA verification.');
          setIsSubmitting(false);
          return;
        }
        loginData.captcha = captchaValue;
      }

      const user = await login(loginData);

      if (!user) {
        setSubmitError('An unexpected error occurred during login. Please try again.');
        return;
      }

      await new Promise(resolve => setTimeout(resolve, 100));

      try {
        await refreshPermissions();
      } catch (permError) {
        // Don't block login if permissions fail
      }

      redirectByRole(user?.role, navigate, user?.user_type);

    } catch (error) {
      const status = error.response?.status;
      const data = error.response?.data;

      if (status === 429) {
        if (data?.requiresCaptcha && RECAPTCHA_SITE_KEY) {
          // Server says too many failed attempts -- show CAPTCHA
          setRequiresCaptcha(true);
          setSubmitError('Too many failed attempts. Please complete the verification below.');
        } else {
          const retryAfter = data?.retryAfter;
          const minutes = retryAfter ? Math.ceil(retryAfter / 60) : 60;
          setSubmitError(`Too many login attempts. Please wait ${minutes} minutes before trying again.`);
        }
      } else if (status === 400 && data?.message?.includes('CAPTCHA')) {
        // CAPTCHA verification failed on the server
        setSubmitError('CAPTCHA verification failed. Please try again.');
        setCaptchaValue(null);
        captchaRef.current?.reset();
      } else if (status === 401) {
        setSubmitError('Invalid email or password. Please check your credentials and try again.');
      } else if (status === 500) {
        setSubmitError('Server error. Please try again later or contact support.');
      } else if (error.code === 'NETWORK_ERROR' || !error.response) {
        setSubmitError('Network error. Please check your internet connection and try again.');
      } else {
        setSubmitError('Login failed. Please try again.');
      }

      setFormData(prev => ({ ...prev, password: '' }));

      setTimeout(() => {
        passwordRef.current?.focus();
      }, 100);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <h2 className="mt-6 text-center text-2xl font-bold text-gray-900">
        Sign in to your account
      </h2>

      {submitError && (
        <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative" role="alert">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium">{submitError}</p>
            </div>
          </div>
        </div>
      )}

      <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
            Email address
          </label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={formData.email}
            onChange={handleChange}
            className="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          />
        </div>

        <div>
          <div className="flex justify-between">
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
              Password
            </label>
            <Link to="/forgot-password" className="text-sm text-blue-600 hover:text-blue-500">
              Forgot your password?
            </Link>
          </div>
          <input
            ref={passwordRef}
            id="password"
            name="password"
            type="password"
            autoComplete="current-password"
            required
            value={formData.password}
            onChange={handleChange}
            className="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          />
        </div>

        {requiresCaptcha && RECAPTCHA_SITE_KEY && (
          <div className="flex justify-center">
            <ReCAPTCHA
              ref={captchaRef}
              sitekey={RECAPTCHA_SITE_KEY}
              onChange={(value) => {
                setCaptchaValue(value);
                if (submitError.includes('CAPTCHA') || submitError.includes('verification')) {
                  setSubmitError('');
                }
              }}
              onExpired={() => {
                setCaptchaValue(null);
              }}
            />
          </div>
        )}

        <div>
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
          >
            {isSubmitting ? (
              <div className="flex items-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Signing in...
              </div>
            ) : 'Sign in'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Login;
